/**
 * Troca cores dentro de um arquivo do Rive, nos bytes, antes de o runtime abrir.
 *
 * O `document-stack.riv` não tem view model: nenhuma cor dele é publicada como
 * propriedade, então o runtime não oferece nada para escrever. As cores moram em
 * objetos `SolidColor` do binário, e é ali que mexemos.
 *
 * Um `SolidColor` sempre aparece assim no fluxo de objetos do .riv:
 *
 *     00           fim do objeto anterior
 *     12           typeKey 18 = SolidColor
 *     05 <varuint> propriedade 5 = parentId (o Fill/Stroke dono da cor)
 *     25 <4 bytes> propriedade 37 = colorValue, uint32 little-endian (B G R A)
 *
 * A busca é ancorada nesse cabeçalho, e não no byte `25` solto: 0x25 aparece o
 * tempo todo no meio de coordenadas float, e a varredura crua acharia lixo.
 *
 * A alternativa seria expor as cores num view model, pelo editor do Rive. Seria
 * mais limpo, mas prenderia toda troca de cor a uma edição no arquivo, fora do
 * alcance do repositório. Aqui a paleta é código e vem do design system.
 */

/** typeKey do objeto `SolidColor`. */
const SOLID_COLOR_TYPE_KEY = 0x12;

/** Propriedade `parentId`, sempre a primeira de um `SolidColor`. */
const PARENT_ID_KEY = 0x05;

/** Propriedade `colorValue`, os 4 bytes que este arquivo existe para trocar. */
const COLOR_VALUE_KEY = 0x25;

/** Lê um varuint (LEB128) e devolve o valor com a posição seguinte. */
function readVarUint(bytes: Uint8Array, at: number): { value: number; next: number } {
    let value = 0;
    let shift = 0;
    let index = at;

    for (;;) {
        const byte = bytes[index++];
        value |= (byte & 0x7f) << shift;
        if ((byte & 0x80) === 0) return { value, next: index };
        shift += 7;
    }
}

/**
 * Onde começa o uint32 de cor de cada `SolidColor`, na ordem em que eles
 * aparecem no arquivo. É essa ordem que a paleta usa como índice.
 */
export function findRiveColorOffsets(bytes: Uint8Array): number[] {
    const offsets: number[] = [];

    for (let i = 0; i + 3 < bytes.length; i++) {
        if (bytes[i] !== 0x00) continue;
        if (bytes[i + 1] !== SOLID_COLOR_TYPE_KEY) continue;
        if (bytes[i + 2] !== PARENT_ID_KEY) continue;

        const { next } = readVarUint(bytes, i + 3);
        if (bytes[next] === COLOR_VALUE_KEY) offsets.push(next + 1);
    }

    return offsets;
}

/**
 * Devolve uma cópia do arquivo com as cores trocadas.
 *
 * A chave de `colors` é o índice do `SolidColor` (ver `findRiveColorOffsets`) e
 * o valor é 0xAARRGGBB. Índice que não existe no arquivo é ignorado em silêncio:
 * um .riv atualizado pelo designer deve estragar a cor, nunca a tela toda.
 */
export function recolorRive(source: ArrayBuffer, colors: ReadonlyMap<number, number>): ArrayBuffer {
    const bytes = new Uint8Array(source.slice(0));
    const offsets = findRiveColorOffsets(bytes);

    for (const [index, argb] of colors) {
        const at = offsets[index];
        if (at === undefined) continue;

        bytes[at] = argb & 0xff;
        bytes[at + 1] = (argb >>> 8) & 0xff;
        bytes[at + 2] = (argb >>> 16) & 0xff;
        bytes[at + 3] = (argb >>> 24) & 0xff;
    }

    return bytes.buffer;
}
